export const SPELLS = {
  EARTHQUAKE: {
    id: "EARTHQUAKE",
    name: "Earthquake",
    description: "Stun character(s) for one turn",
    gameState: "earthquake",
    url: "/units/earthquake",
  },
  RAIN_OF_FIRE: {
    id: "RAIN_OF_FIRE",
    name: "Rain of Fire",
    description: "Deal damage to character(s), anywhere on the map",
    damage: 15,
    targets: 1,
    gameState: "rainoffire",
    url: "/units/rainoffire",
  },
  WHIRLWIND: {
    id: "WHIRLWIND",
    name: "Whirlwind",
    description: "Push all units away from a chosen location on the map",
    gameState: "whirlwind",
    url: "/units/whirlwind",
  },
};

// Export an array of all spell game states for easy access
export const SPELL_GAME_STATES = Object.values(SPELLS).map(
  (spell) => spell.gameState
);

// Get initial spell selection (just the spell IDs, no cooldowns)
export function getInitialSpellSelection() {
  return Object.keys(SPELLS);
}

// Initialize spell cooldowns for only unlocked spells based on player upgrades
export function initializeSpellCooldowns(playerUpgrades) {
  const cooldowns = {};
  const unlockedSpells = playerUpgrades
    .filter((upgrade) => Object.keys(SPELLS).includes(upgrade.upgrade))
    .map((upgrade) => upgrade.upgrade);

  unlockedSpells.forEach((spellId) => {
    cooldowns[spellId] = 0; // Start with no cooldown for unlocked spells
  });
  return cooldowns;
}

// Get unlocked spells from player upgrades
export function getUnlockedSpells(playerUpgrades) {
  return playerUpgrades
    .filter((upgrade) => Object.keys(SPELLS).includes(upgrade.upgrade))
    .map((upgrade) => upgrade.upgrade);
}

// Level configuration for each spell to enable scalable future levels
// Level 1 corresponds to the base unlocked spell (key === spell id)
// Higher levels correspond to upgrades with keys like `${ID}_L2`, `${ID}_L3`, etc.
const SPELL_LEVELS = {
  EARTHQUAKE: {
    1: { targets: 1, cooldown: 4 },
    2: { targets: 2, cooldown: 3 },
    3: { targets: 3, cooldown: 3 },
  },
  RAIN_OF_FIRE: {
    1: { targets: 1, damage: 15, cooldown: 4 },
    2: { targets: 2, damage: 18, cooldown: 3 },
    3: { targets: 3, damage: 20, cooldown: 3 },
  },
  WHIRLWIND: {
    1: { cooldown: 3, range: 9 },
    2: { cooldown: 2, range: 9 },
    3: { cooldown: 2, rangeMin: 5, rangeMax: 15 },
  },
};

// Determine a player's spell level for a given spell id based on unlocked upgrade keys
export function getSpellLevel(spellId, unlockedUpgradeKeys = []) {
  let level = unlockedUpgradeKeys.includes(spellId) ? 1 : 0;
  const regex = new RegExp(`^${spellId}_L(\\d+)$`);
  for (const key of unlockedUpgradeKeys) {
    const match = key.match(regex);
    if (match) {
      const n = parseInt(match[1], 10);
      if (!Number.isNaN(n)) level = Math.max(level, n);
    }
  }
  return level;
}

// Get effective properties for a spell given the player's unlocked upgrades
export function getEffectiveSpellProps(spellId, unlockedUpgradeKeys = []) {
  const base = SPELLS[spellId] || {};
  const level = getSpellLevel(spellId, unlockedUpgradeKeys);
  const levelTable = SPELL_LEVELS[spellId] || {};

  // Resolve properties for the highest defined level up to player's level
  let resolved = {};
  for (let l = 1; l <= level; l++) {
    if (levelTable[l]) {
      resolved = { ...resolved, ...levelTable[l] };
    }
  }

  return { ...base, ...resolved, level };
}
